<template>
  <div v-if="!edit && !access && listing">
    <div class="d-flex justify-content-between pt-4 mb-2">
      <div class="d-flex align-items-center">
        <div
          v-if="listing && listing.photoUrl"
          class="photo-wrapper"
        >
          <img
            ref="photo"
            class="photo"
            :src="listing.photoUrl"
          >
        </div>

        <div class="product-name">
          {{ listing.name }}
        </div>
      </div>
      <div class="d-flex">
        <div
          v-if="listing"
          class="mr-2"
        >
          <Tippy
            interactive
            arrow
            theme="light"
            trigger="click"
          >
            <template #trigger>
              <i
                v-tippy
                class="fas fa-trash secondary-icon"
                :content="$t('general.remove')"
              />
            </template>

            <div v-if="!pendingRemove">
              <button
                class="btn btn-block btn-danger"
                @click="remove"
              >
                <i class="fas fa-trash pr-2" />
                {{ $t('general.confirmRemove') }}
              </button>
            </div>
            <div v-else>
              <Loader size="22px" />
            </div>
          </Tippy>
        </div>
        <div class="mr-2">
          <i
            v-tippy
            class="fas fa-user secondary-icon smaller-icon"
            :content="$t('settings.access')"
            @click="access = true"
          />
        </div>
        <div>
          <i
            v-tippy
            class="fas fa-pen secondary-icon smaller-icon"
            :content="$t('general.edit')"
            @click="edit = true"
          />
        </div>
      </div>
    </div>

    <div
      v-if="listing.description"
      class="small text-secondary rich-description"
      v-html="listing.description"
    />
    <hr>
    <div class="d-flex justify-content-between align-items-center mt-4">
      <div class="section-title">
        {{ $t('shop.price') }}
      </div>

      <div class="money-font">
        {{ formatCurrency(listing.price, listing.currency) }}
      </div>
    </div>

    <template v-if="catalogDetails.billPerListing">
      <div
        v-if="listing.priceType && listing.priceType !== 'Total'"
        class="d-flex justify-content-between group-price first px-2 mt-1"
      >
        <div class="text-secondary small">
          {{ $t('shop.listing.priceType') }}
        </div>
        <div class="small">
          {{ $t(`shop.listing.priceTypes.${priceTypeKey(listing.priceType)}`) }}
        </div>
      </div>

      <template
        v-if="
          (listing.vatCode && listing.vatCode !== 'Unknown') ||
            listing.accountingCode ||
            listing.billingCategoryId ||
            listing.accountNumber
        "
      >
        <div class="section-title mt-4 mb-2">
          {{ $t('shop.listing.billing') }}
        </div>
        <div
          v-if="listing.vatCode && listing.vatCode !== 'Unknown'"
          class="d-flex justify-content-between group-price first px-2"
        >
          <div class="text-secondary small">
            {{ $t('shop.listing.vatCode') }}
          </div>
          <div class="small">
            {{ vatCodeFormat }}
          </div>
        </div>
        <div
          v-if="listing.accountingCode"
          class="d-flex justify-content-between group-price px-2"
          :class="{ first: !listing.vatCode }"
        >
          <div class="text-secondary small">
            {{ $t('shop.listing.accountingCode') }}
          </div>
          <div class="small">
            {{ listing.accountingCode }}
          </div>
        </div>
        <div
          v-if="listing.billingCategoryId"
          class="d-flex justify-content-between group-price px-2"
          :class="{ first: !listing.vatCode && !listing.accountingCode }"
        >
          <div class="text-secondary small">
            {{ $t('bill.category') }}
          </div>
          <div class="small">
            {{ categoryName(listing.billingCategoryId) }}
          </div>
        </div>
        <div
          v-if="listing.accountNumber"
          class="d-flex justify-content-between group-price px-2"
          :class="{ first: !listing.vatCode && !listing.accountingCode && !listing.billingCategoryId }"
        >
          <div class="text-secondary small">
            {{ $t('payment.accountNumber') }}
          </div>
          <div class="small money-font">
            {{ listing.accountNumber }}
          </div>
        </div>
      </template>
    </template>
    <hr>

    <template v-if="catalogDetails.productCategory === 'Courses'">
      <div class="section-title mt-4 mb-2">
        Harmonogram
      </div>
      <div
        v-for="rule in listingRules"
        :key="rule.id"
        class="d-flex justify-content-between group-price px-2"
        :class="{ first: rule === listingRules[0] }"
      >
        <span class="font-weight-bold">{{ formatWeekDay(rule.weekDay) }}</span>
        <div class="text-right">
          <div class="text-secondary">
            {{ formatHour(rule.startMinutes) }} – {{ formatHour(rule.startMinutes + rule.durationMinutes) }}
          </div>
          <div
            v-if="rule.comment"
            class="small text-muted"
          >
            {{ rule.comment }}
          </div>
        </div>
      </div>
      <div
        v-if="listingRules.length === 0"
        class="group-price px-2 first text-secondary small"
      >
        -
      </div>
    </template>

    <template v-if="listing.productIds.length > 0">
      <div class="section-title mt-4 mb-3">
        {{ $t('shop.products') }}
      </div>
      <div class="group-price px-2 first">
        <div
          v-for="productId in listing.productIds"
          :key="productId"
        >
          <span>
            {{ productName(productId) }}
          </span>
        </div>
      </div>
    </template>

    <div
      v-for="field in catalogDetails.listingFields"
      :key="field.id"
    >
      <div class="section-title mt-3 mb-2">
        {{ field.name }}
      </div>
      <div class="group-price px-2 first">
        {{ listing.fields[field.fieldId] || '-' }}
      </div>
    </div>

    <div
      v-for="subCatalog in subCatalogsSorted"
      :key="subCatalog.id"
    >
      <div class="section-title mt-3 mb-2">
        {{ subCatalog.name }}
      </div>
      <div class="group-price px-2 first">
        <div v-if="subCatalog.nodes.length === 0">
          -
        </div>
        <div
          v-for="node in subCatalog.nodes"
          :key="node.nodeId"
          class="d-flex justify-content-between"
        >
          {{ node.name }}
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center justify-content-between mt-4 mb-3">
      <div class="section-title">
        <i class="fas fa-square-1 text-secondary pr-2" />
        {{ $t('shop.listing.individualOrders') }}
      </div>

      <div
        class="badge"
        :class="individualOrdersEnabled ? 'badge-primary' : 'badge-danger'"
      >
        <template v-if="individualOrdersEnabled">
          <i class="fas fa-circle-check pr-1" />
          {{ $t('shop.listing.enabled') }}
        </template>
        <template v-else>
          <i class="fas fa-ban pr-1" />
          {{ $t('shop.listing.blocked') }}
        </template>
      </div>
    </div>

    <div
      v-for="(limit, i) in timeLimits"
      :key="limit.label"
      class="d-flex justify-content-between group-price px-2"
      :class="{ first: i === 0 }"
    >
      <div class="text-secondary font-weight-bold">
        <i
          :class="timeIcon(limit.time)"
          class="pr-2"
        />
        {{ limit.label }}
      </div>
      <div>
        <span
          v-if="singleOrderSchema"
          class="small"
        >
          {{ formatTime(limit.time) }}
        </span>
        <TimeReferenceBadge
          v-else
          :value="limit.time"
          details
        />
      </div>
    </div>

    <div v-if="listing.subscription && listing.subscription.enabled">
      <div class="d-flex align-items-center justify-content-between mt-4 mb-3">
        <div class="section-title">
          <i class="fas fa-repeat text-secondary pr-2" />
          {{ $t('shop.subscription.subscription') }}
        </div>

        <div class="badge badge-primary">
          <i class="fas fa-circle-check pr-1" />
          {{ $t('shop.sub.enabled') }}
        </div>
      </div>

      <div class="group-price first px-2">
        <div
          v-if="listing.subscription.applyAt"
          class="d-flex my-1"
        >
          <div class="small w-75">
            {{ $t('shop.subscription.applyAt') }}:
          </div>
          <TimeReferenceBadge
            :value="listing.subscription.applyAt"
            details
            style="min-width: 136px"
          />
        </div>

        <div
          v-if="listing.subscription.includeUntil"
          class="d-flex my-1"
        >
          <div class="small w-75">
            {{ $t('shop.subscription.includeUntil') }}:
          </div>

          <TimeReferenceBadge
            :value="listing.subscription.includeUntil"
            details
            style="min-width: 136px"
          />
        </div>

        <div
          v-if="listing.subscription.subscribeFrom"
          class="d-flex my-1 small"
        >
          <div class="w-75">
            {{ $t('shop.subscription.subscribeFrom') }}:
          </div>

          <TimeReferenceBadge
            :value="listing.subscription.subscribeFrom"
            details
            date
            style="min-width: 136px"
          />
        </div>

        <div
          v-if="listing.subscription.subscribeTo"
          class="d-flex my-1 small"
        >
          <div class="w-75">
            {{ $t('shop.subscription.subscribeTo') }}:
          </div>

          <TimeReferenceBadge
            :value="listing.subscription.subscribeTo"
            details
            date
            style="min-width: 136px"
          />
        </div>

        <div
          v-if="listing.subscription.unsubscribeTo"
          class="d-flex my-1 small"
        >
          <div class="w-75">
            {{ $t('shop.subscription.unsubscribeTo') }}:
          </div>

          <TimeReferenceBadge
            :value="listing.subscription.unsubscribeTo"
            details
            date
            style="min-width: 136px"
          />
        </div>
      </div>

      <div
        v-if="listing.subscriptionRun"
        class="small mt-2 text-secondary"
      >
        <div>
          {{ $t('shop.sub.nextRun') }}:
          <span class="font-weight-bold">
            {{ applyAtFormatted }}
          </span>
        </div>
        <div>
          {{ $t('shop.sub.monthRun') }}:
          <span class="font-weight-bold">
            {{ nextMonth }}
          </span>
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div
        v-if="listing.countLimit >= 0"
        class="d-flex justify-content-between first group-price px-2"
        :class="{ first: timeLimits.length === 0 }"
      >
        <div class="text-secondary font-weight-bold">
          <i class="fa-solid fa-circle-1 pr-2" />
          {{ $t('shop.listing.limitCount') }}
        </div>
        <div>
          <span class="small">
            {{ listing.countLimit }}
          </span>
        </div>
      </div>
      <div
        v-if="selectedForm"
        class="d-flex justify-content-between group-price px-2"
        :class="{ first: timeLimits.length === 0 && listing.countLimit <= 0 }"
      >
        <div class="text-secondary font-weight-bold">
          <i class="fa-solid fa-file-lines pr-2" />
          {{ $t('shop.listing.form') }}
        </div>
        <div>
          <span class="small">{{ selectedForm.name }}</span>
        </div>
      </div>
    </div>
  </div>
  <ProductListingAccess
    v-else-if="access"
    :listing="listing"
    @close="closeForm"
  />
  <ProductListingForm
    v-else
    :listing="listing"
    @close="closeForm"
  />
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import formatVatCode from '@/utils/vat';
import TimeReferenceBadge from '../TimeReferenceBadge';
import ProductListingAccess from './ProductListingAccess';
import ProductListingForm from './ProductListingForm';

export default {
  props: {
    listing: Object,
  },
  data: () => ({
    edit: false,
    access: false,
    pendingRemove: false,
    photo: null,
    listingRules: [],
    forms: [],
  }),
  components: {
    ProductListingForm,
    ProductListingAccess,
    TimeReferenceBadge,
  },
  computed: {
    ...mapGetters(['formatCurrency', 'groups', 'payee']),
    ...mapGetters('shop', ['products', 'catalogDetails', 'singleOrderSchema']),
    selectedForm() {
      if (!this.listing?.form) return null;
      return this.forms.find((f) => f.id === this.listing.form) || null;
    },
    vatCodeFormat() {
      return formatVatCode(this.listing.vatCode);
    },
    nextMonth() {
      return moment(this.listing?.subscriptionRun?.applyAt)
        .add(this.listing?.subscriptionRun?.monthOffset, 'month')
        .format('MMMM YYYY');
    },
    applyAtFormatted() {
      return moment(this.listing?.subscriptionRun?.applyAt).format('DD MMMM YYYY HH:mm');
    },
    individualOrdersEnabled() {
      if (!this.listing) return false;
      return (
        this.listing.orderFrom !== 'Absolute|0.00:00:00.false'
          || this.listing.orderTo !== 'Absolute|0.00:00:00.false'
          || this.listing.cancelTo !== 'Absolute|0.00:00:00.false'
      );
    },
    timeLimits() {
      return [
        {
          time: this.listing.orderFrom,
          label: this.$t('shop.orderFrom'),
        },
        {
          time: this.listing.orderTo,
          label: this.$t('shop.orderTo'),
        },
        {
          time: this.listing.cancelTo,
          label: this.$t('shop.cancelTo'),
        },
      ].filter((x) => x.time && x.time !== 'Absolute|0.00:00:00.false');
    },
    subCatalogsSorted() {
      return this.catalogDetails.subCatalogs
        .slice()
        .filter(({ subCatalogId }) => subCatalogId !== '#products')
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((x) => ({
          ...x,
          nodes: this.listing.assignments
            .filter(({ subCatalogId }) => subCatalogId === x.subCatalogId)
            .map(({ subCatalogId, nodeId }) => {
              if (subCatalogId === '#groups') {
                const group = this.groups.find((g) => g.code === nodeId);
                if (!group) return null;
                return {
                  nodeId: group.code,
                  name: group.code === '#all' ? this.$t('company.all') : group.path,
                };
              }

              return this.catalogDetails.subCatalogNodes
                .filter((n) => n.subCatalogId === x.subCatalogId)
                .find((s) => s.nodeId === nodeId);
            })
            .filter((n) => n)
            .sort((a, b) => {
              if (x.subCatalogId === '#groups') {
                return a.name.localeCompare(b.name);
              }

              return a.ordinal - b.ordinal;
            }),
        }));
    },
    sortedAssignments() {
      return this.listing.assignments
        .map(({ subCatalogId, nodeId }) => {
          if (subCatalogId === '#groups') {
            const group = this.groups.find((g) => g.code === nodeId);
            return {
              nodeId: group.code,
              nodeName: group.path,
            };
          }

          const node = this.catalogDetails.subCatalogNodes.find((s) => s.nodeId === nodeId);
          return {
            nodeId: node?.nodeId,
            nodeName: node?.name,
          };
        })
        .filter((x) => x.nodeId);
    },
  },
  watch: {
    listing() {
      this.edit = false;
      this.loadRules();
    },
  },
  methods: {
    ...mapActions(['getFormDefinitions']),
    ...mapActions('shop', ['deleteCatalogListing', 'getCalendarRules']),
    loadRules() {
      if (this.catalogDetails.productCategory !== 'Courses') return;
      this.getCalendarRules({ params: { catalogId: this.catalogDetails.id } }).then(({ data }) => {
        this.listingRules = data.filter((r) => r.offerId === this.listing.id);
      });
    },
    formatWeekDay(weekDay) {
      const days = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
      return days[weekDay] || weekDay;
    },
    formatHour(minutes) {
      const hour = Math.floor(minutes / 60)
        .toString()
        .padStart(2, '0');
      const minute = (minutes % 60).toString().padStart(2, '0');
      return `${hour}:${minute}`;
    },
    remove() {
      this.pendingRemove = true;

      this.deleteCatalogListing({
        params: {
          catalogId: this.catalogDetails.id,
          listingId: this.listing.id,
        },
      })
        .then(() => {
          this.$emit('close');
        })
        .finally(() => {
          this.pendingRemove = false;
        });
    },
    closeForm() {
      this.edit = false;
      this.access = false;
      if (!this.listing) {
        this.$emit('close');
      }
    },
    formatTime(date) {
      return moment(date, 'YYYY-MM-DD HH:mm').format('DD MMMM YYYY HH:mm');
    },
    productName(productId) {
      return this.products.find((x) => x.id === productId)?.name;
    },
    subCatalogName(id) {
      return this.catalogDetails.subCatalogNodes.find((x) => x.subCatalogId === id)?.name;
    },
    subCatalogNodeName(id) {
      return this.catalogDetails.subCatalogs.find((x) => x.subCatalogId === id)?.name;
    },
    timeIcon() {
      return 'fas fa-calendar';
    },
    priceTypeKey(priceType) {
      const map = { Total: 'total', ForEvent: 'forEvent', ForParticipation: 'forParticipation' };
      return map[priceType] || 'total';
    },
    categoryName(id) {
      return this.payee?.categories?.find((x) => x.id === id)?.name || id;
    },
  },
  created() {
    this.loadRules();
    this.getFormDefinitions().then(({ data }) => {
      this.forms = data.filter((x) => x.type === 'ShopListing');
    });
  },
};
</script>

<style lang="scss" scoped>
  .product-name {
    font-size: 20px;
    font-weight: 500;
    line-height: 1.3;
  }

  .photo {
    max-height: 70px;
    max-width: 70px;
  }

  .photo-wrapper {
    width: 70px;
    height: 70px;
    min-width: 70px;
    min-height: 70px;
    border: 1px solid #efefef;
    border-radius: 5px;
    overflow: hidden;
    text-align: center;
    margin-right: 20px;
  }

  .assignment-badge {
    color: #fff;
    background-color: rgba($blue, 0.4);
  }

  .group-price {
    padding: 5px;
    border: 1px solid #eee;
    border-top: none;

    &.first {
      border-top: 1px solid #eee;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 500;
  }

  .rich-description {
    ::v-deep p:last-child {
      margin-bottom: 0;
    }

    ::v-deep ul,
    ::v-deep ol {
      padding-left: 1.25rem;
      margin-bottom: 0.25rem;
    }
  }
</style>
